'use client';

import { usePathname } from 'next/navigation';
import { SmartLink } from '@/components/smart-link';
import { siteChrome } from '@/lib/site-chrome';

export interface NavLinkProps {
  href: string;
  label: string;
  /** Applied on every state. */
  className: string;
  /** Added when the visitor is on this link's page (or below it). */
  activeClassName: string;
}

/** Every path the menus point at, so a link can tell when a sibling is the better match. */
const NAV_TARGETS = siteChrome.menus.flatMap((menu) =>
  menu.groups.flatMap((group) => [
    ...(group.headingHref ? [group.headingHref] : []),
    ...group.links.map((link) => link.href),
  ]),
);

/** A link owns the page it points at and everything under it. */
const covers = (path: string, candidate: string) =>
  candidate === '/' ? path === '/' : path === candidate || path.startsWith(`${candidate}/`);

/**
 * Nav menu link that marks itself as the page you're on. Matching includes
 * descendants so a program category still lights up on its own sub-pages, but
 * the most specific link wins — /about/best-team highlights "Our Team", not
 * the "Our Story" link that merely prefixes it.
 */
export function NavLink({ href, label, className, activeClassName }: NavLinkProps) {
  const current = usePathname() ?? '/';
  const mine = href;
  const active =
    covers(current, mine) &&
    !NAV_TARGETS.some((other) => other.length > mine.length && covers(current, other));

  return (
    <SmartLink
      href={href}
      className={`${className} ${active ? activeClassName : ''}`}
      {...(active ? { 'aria-current': 'page' as const } : {})}
    >
      {label}
    </SmartLink>
  );
}
